/**
 * TIMER SYSTEM - Sistema de Temporizador
 * Maneja el tiempo de cada ronda
 */

import { ROUND_DEFAULTS } from "../utils/constants.js";

export default class TimerSystem {
  constructor(scene, initialTime = ROUND_DEFAULTS.INITIAL_TIME) {
    this.scene = scene;
    this.initialTime = initialTime;
    this.timeLeft = initialTime;
    this.isRunning = false;
    this.isPaused = false;
    this.timerEvent = null;
  }

  /**
   * Iniciar el temporizador
   */
  start() {
    if (this.isRunning) return;

    this.isRunning = true;
    this.isPaused = false;

    // Crear evento de timer que se ejecuta cada segundo
    this.timerEvent = this.scene.time.addEvent({
      delay: 1000, // 1 segundo
      callback: this.tick,
      callbackScope: this,
      loop: true,
    });

    this.onTimerStarted();
  }

  /**
   * Pausar el temporizador
   */
  pause() {
    if (!this.isRunning || this.isPaused) return;

    this.isPaused = true;
    if (this.timerEvent) {
      this.timerEvent.paused = true;
    }

    this.onTimerPaused();
  }

  /**
   * Reanudar el temporizador
   */
  resume() {
    if (!this.isRunning || !this.isPaused) return;

    this.isPaused = false;
    if (this.timerEvent) {
      this.timerEvent.paused = false;
    }

    this.onTimerResumed();
  }

  /**
   * Detener el temporizador
   */
  stop() {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.isPaused = false;

    if (this.timerEvent) {
      this.timerEvent.remove();
      this.timerEvent = null;
    }

    this.onTimerStopped();
  }

  /**
   * Tick del temporizador (llamado cada segundo)
   */
  tick() {
    // Verificar AMBOS: isRunning e isPaused
    if (!this.isRunning || this.isPaused) return;

    this.timeLeft--;

    // Emit evento cada segundo
    this.onTimerTick();

    // Verificar si se acabó el tiempo
    if (this.timeLeft <= 0) {
      this.timeLeft = 0;
      this.stop();
      this.onTimeEnd();
    }
  }

  /**
   * Obtener tiempo restante
   * @returns {number} Segundos restantes
   */
  getTimeLeft() {
    return this.timeLeft;
  }

  /**
   * Obtener tiempo inicial
   * @returns {number}
   */
  getInitialTime() {
    return this.initialTime;
  }

  /**
   * Obtener tiempo transcurrido
   * @returns {number}
   */
  getElapsedTime() {
    return this.initialTime - this.timeLeft;
  }

  /**
   * Verificar si el tiempo se está acabando (< 10 segundos)
   * @param {number} threshold - Umbral en segundos (default: 10)
   * @returns {boolean}
   */
  isRunningOut(threshold = 10) {
    return this.timeLeft <= threshold && this.timeLeft > 0;
  }

  /**
   * Verificar si el tiempo se acabó
   * @returns {boolean}
   */
  isTimeUp() {
    return this.timeLeft <= 0;
  }

  /**
   * Agregar tiempo (bonus, powerup, etc)
   * @param {number} seconds - Segundos a agregar
   */
  addTime(seconds) {
    this.timeLeft += seconds;
    this.onTimeAdded(seconds);
  }

  /**
   * Resetear el temporizador
   * @param {number} newTime - Nuevo tiempo inicial (opcional)
   */
  reset(newTime = null) {
    this.stop();

    if (newTime !== null) {
      this.initialTime = newTime;
    }

    this.timeLeft = this.initialTime;
    this.onTimerReset();
  }

  /**
   * Callback cuando inicia el temporizador
   */
  onTimerStarted() {
    this.scene.events.emit("timerStarted", this.timeLeft);
  }

  /**
   * Callback cuando se pausa el temporizador
   */
  onTimerPaused() {
    this.scene.events.emit("timerPaused", this.timeLeft);
  }

  /**
   * Callback cuando se reanuda el temporizador
   */
  onTimerResumed() {
    this.scene.events.emit("timerResumed", this.timeLeft);
  }

  /**
   * Callback cuando se detiene el temporizador
   */
  onTimerStopped() {
    this.scene.events.emit("timerStopped", this.timeLeft);
  }

  /**
   * Callback cada tick (segundo)
   */
  onTimerTick() {
    this.scene.events.emit("timerTick", this.timeLeft);
  }

  /**
   * Callback cuando se acaba el tiempo
   */
  onTimeEnd() {
    this.scene.events.emit("timeEnd");
  }

  /**
   * Callback cuando se agrega tiempo
   */
  onTimeAdded(seconds) {
    this.scene.events.emit("timeAdded", {
      added: seconds,
      current: this.timeLeft,
    });
  }

  /**
   * Callback cuando se resetea el temporizador
   */
  onTimerReset() {
    this.scene.events.emit("timerReset", this.initialTime);
  }

  /**
   * Destruir el sistema
   */
  destroy() {
    this.stop();
    this.scene = null;
  }

  /**
   * Pausar el timer
   */
  pause() {
    this.isRunning = false;
    console.log("⏸ Timer pausado");
  }

  /**
   * Reanudar el timer
   */
  resume() {
    this.isRunning = true;
    console.log("▶️ Timer reanudado");
  }
}
